import { useState } from "react";
import { Button } from "../common/Button";
import { Field } from "../common/Field";
import { Modal } from "../common/Modal";

/**
 * Create and retire the author's own journal tags (#464).
 *
 * DELETING IS SOFT, AND THE COPY HAS TO SAY SO. The tag stops being
 * offered and stops being a filter; entries already carrying it keep it.
 * That is the safe behaviour, but it is not what "Delete" implies -- and
 * a user who believes deleting a tag strips it from forty entries will
 * hesitate over a button that is actually harmless. So the button says
 * Retire and the line underneath says what happens.
 *
 * No confirmation dialog, deliberately. ConfirmDialog is for things that
 * cannot be undone; retiring a tag is undone by creating it again with
 * the same name, which revives the original row and its entries. A
 * confirmation on a reversible action is noise that trains people to
 * click through the ones that matter.
 */
export function JournalTagManager({ open, tags, onClose, onCreate, onRetire }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function create() {
    const text = name.trim();
    if (!text) return;
    setBusy(true);
    try {
      await onCreate(text);
      setName("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Your journal tags">
      <p style={{ color: "var(--color-text-muted)", maxWidth: "52ch" }}>
        Tags are yours alone. They stay on your own entries and are not
        shown to your team, even on an entry you have shared.
      </p>

      <Field label="New tag">
        {(id, aria) => (
          <input
            id={id}
            {...aria}
            className="touch-target"
            style={{ width: "100%" }}
            maxLength={40}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="varroa, spring, north-yard…"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
          />
        )}
      </Field>
      <Button onClick={create} disabled={busy || !name.trim()}>
        {busy ? "Adding…" : "Add tag"}
      </Button>

      {tags.length === 0 ? (
        <p style={{ marginTop: "var(--space-3)" }}>No tags yet.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, marginTop: "var(--space-3)" }}>
          {tags.map((t) => (
            <li
              key={t.tag_id}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: "var(--space-2)", padding: "var(--space-1) 0",
              }}
            >
              <span>{t.name}</span>
              <Button size="sm" variant="secondary" onClick={() => onRetire(t.tag_id)}>
                Retire
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        Retiring a tag stops it being offered. Entries already tagged with
        it keep it, and adding the tag again brings it back.
      </p>
    </Modal>
  );
}
